import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin, Typography } from "antd";

import { fetchPatients } from "../../../api/healthcare/PatientApi";
import { Patient } from "../../../types/healthcare/PatientTypes";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import {
	PatientSearchRunner,
	buildPatientOptions,
	createPatientSearchRunner,
} from "./patientSearch";

const { Text } = Typography;

/**
 * The patient picker the invoicing screens use, wherever the old UI had a free-text "User ID"
 * input.
 *
 * ── On "reuse the appointment-create control" ─────────────────────────────────────────────────
 *
 * There is no shared component to reuse: the appointment create/edit surface
 * (`pages/healthcare/appointments/appDrawerComponents/GeneralTab.tsx`) builds the control inline,
 * and so do `CreateTreatmentPlanModal` and `MessagingPanel`. What is genuinely shared is the
 * BEHAVIOUR, and it is reproduced here exactly:
 *
 *   - the same search function, `fetchPatients` — which POSTs the term in the request BODY, never
 *     the query string, because nginx's combined log format writes `$request` to disk and a
 *     patient name in a URL is a name in a plaintext access log;
 *   - the same 300 ms debounce, with the timer cleared on unmount;
 *   - the same page size, `loadUserPreferences().tableRowsPerPage`.
 *
 * What is deliberately NOT reproduced is GeneralTab's `AutoComplete` shape, where the control's
 * value is a display STRING and the real patient rides on `option.patient`. An invoice needs an
 * id in a `Form.Item`, so this is a `Select` whose value IS the id — the same shape
 * `CreateTreatmentPlanModal` chose. Nor is the "create new patient" shortcut: raising an invoice
 * is not the place to create a patient record.
 *
 * ⚠️ The debounce, the stale-response guard and the option building live in `patientSearch.ts`,
 * not here — antd v5's `Select` dropdown cannot be opened under jsdom, so behaviour tested
 * through this widget could not be tested at all. See that file's docblock.
 */

export interface PatientSelectProps {
	/** The auth service's numeric patient id, as a string — `Invoice.userId`'s own type. */
	value?: string;
	onChange?: (value: string | undefined, patient?: Patient) => void;
	placeholder?: string;
	disabled?: boolean;
	allowClear?: boolean;
	style?: React.CSSProperties;
	"data-testid"?: string;
	/**
	 * Rows to seed the list with before anything is typed, so a picker that already has a value
	 * can render its label. Any patient selected during this mount is remembered automatically.
	 */
	initialOptions?: Patient[];
}

const PatientSelect: React.FC<PatientSelectProps> = ({
	value,
	onChange,
	placeholder = "Search name, email or phone…",
	disabled,
	allowClear = true,
	style,
	initialOptions,
	...rest
}) => {
	const [patients, setPatients] = useState<Patient[]>(initialOptions ?? []);
	const [searching, setSearching] = useState<boolean>(false);
	// Keeps the label of an already-selected patient visible after a later search has replaced
	// the results — otherwise the field blanks out while still holding its id.
	const [selected, setSelected] = useState<Patient | null>(null);

	const runnerRef = useRef<PatientSearchRunner | null>(null);
	if (!runnerRef.current) {
		runnerRef.current = createPatientSearchRunner({
			fetch: async (term) => {
				const perPage = loadUserPreferences().tableRowsPerPage;
				const { data } = await fetchPatients(1, perPage, term, "first_name", "asc");
				return data ?? [];
			},
			onResults: setPatients,
			onSearchingChange: setSearching,
		});
	}

	useEffect(() => {
		const runner = runnerRef.current;
		return () => runner?.dispose();
	}, []);

	const options = useMemo(
		() => buildPatientOptions(patients, selected, value),
		[patients, selected, value],
	);

	return (
		<Select
			showSearch
			filterOption={false}
			value={value || undefined}
			onSearch={(term) => runnerRef.current?.search(term)}
			onChange={(next, option) => {
				const picked = (option as { patient?: Patient } | undefined)?.patient ?? null;
				setSelected(picked);
				onChange?.(next as string | undefined, picked ?? undefined);
			}}
			onClear={() => {
				setSelected(null);
				onChange?.(undefined, undefined);
			}}
			options={options}
			placeholder={placeholder}
			disabled={disabled}
			allowClear={allowClear}
			style={{ width: "100%", ...style }}
			notFoundContent={
				searching ? (
					<Spin size="small" />
				) : (
					<Text type="secondary" style={{ fontSize: 12 }}>
						Type to search patients
					</Text>
				)
			}
			{...rest}
		/>
	);
};

export default PatientSelect;
